/**
 * Hand-written FAQs for individual project pages.
 *
 * generatePropertyFAQ templates 17 answers from the record, which is the
 * right default for the ~100 pages nobody has written copy for. On a page
 * someone has written, the generated set gets in the way: it repeats the
 * same generic answers as every other project, and its process answers
 * describe resale-style timing rather than a developer sale.
 *
 * Where a slug appears here, these answers replace the generated set
 * entirely, both on the page and in the FAQPage schema.
 *
 * Stopgap, same as propertyMetaOverrides: the destination is per-property
 * FAQ fields in the CMS. When those land, read them first and delete this.
 *
 * Answers may contain inline HTML links, matching generatePropertyFAQ.
 */

export interface PropertyFaqOverride {
  question: string;
  answer: string;
}

export const PROPERTY_FAQS: Record<string, PropertyFaqOverride[]> = {
  "bukit-timah-road-gls": [
    {
      question: "What is the Bukit Timah Road GLS site?",
      answer:
        "A 99-year residential site next to Newton MRT, sold by the government in November 2025. It will be developed into a private condominium of about 340 homes, by the tender estimate.",
    },
    {
      question: "Who is the developer?",
      answer:
        "HH Investment Private Limited, the Singapore arm of Taiwan's Huang Hsiang Construction. It won the site with a bid of S$566.3 million, about S$1,820 per square foot per plot ratio, ahead of seven other bidders. Its earlier Singapore projects include Balmoral Place, completed in 2000.",
    },
    {
      question: "What is the project called?",
      answer:
        "The developer has not announced a name. We use \"Bukit Timah Road GLS (Newton)\" as a working label and will change it here the day the real name is published.",
    },
    {
      question: "How much will it cost?",
      answer:
        "Prices have not been released. Analysts writing at the time of the land award expected an average of roughly S$3,400 to S$3,600 psf, based on what the land cost. That is a projection, not a price list. Register on this page and we will send the developer's prices when they are out.",
    },
    {
      question: "When does it launch, and when is TOP?",
      answer:
        "Neither has been announced. No preview date, launch date or Temporary Occupation Permit date has been given by the developer.",
    },
    {
      question: "How close is it to the MRT?",
      answer:
        "The site is next to Newton MRT, an interchange between the North-South Line and the Downtown Line. Orchard is one stop away and Novena two.",
    },
    {
      question: "Is this the same as the Peck Hay Road GLS site?",
      answer:
        "No. Peck Hay Road is a separate Newton site across the MRT station, with a different developer. Its tender closed in June 2026 with a top bid of about S$1,865 psf ppr from a City Developments and Hong Realty joint venture, for an estimated 315 homes.",
    },
    {
      question: "Can foreigners buy here?",
      answer:
        "Yes. This is a private condominium, so there is no citizenship restriction and no income ceiling. Foreigners pay Additional Buyer's Stamp Duty at the rate IRAS sets at the time of purchase, on top of Buyer's Stamp Duty.",
    },
  ],
  "chuan-grove": [
    {
      question: "What is the Chuan Grove GLS?",
      answer:
        "A planned 99-year private condo off Lorong Chuan, built on two adjoining government land parcels sold in 2025. Around 1,055 homes in five blocks are planned.",
    },
    {
      question: "Who is the developer?",
      answer:
        "A joint venture of Sing Holdings (65%) and Sunway Developments (35%), the Singapore arm of Malaysia's Sunway group. It won both parcels for a total of S$1.33 billion.",
    },
    {
      question: "What is the project called? Is it \"One Chuan Grove\"?",
      answer:
        "The developer has not announced a name. \"One Chuan Grove\" is used by some agent websites, but it is not confirmed. We will update this page when the developer names it.",
    },
    {
      question: "How much will it cost?",
      answer:
        "Prices have not been released. Based on the land price, analysts expect an average of about S$2,600 to S$2,850 psf. That is a projection, not a price list.",
    },
    {
      question: "When does it launch, and when is TOP?",
      answer:
        "Sing Holdings says it plans to launch by the first quarter of 2027. No preview date or TOP has been announced. Register on this page to be told when the preview opens.",
    },
    {
      question: "How close is it to the MRT?",
      answer:
        "About 400 m from Lorong Chuan MRT on the Circle Line for the first parcel. The second parcel is further away. Bishan and Serangoon interchanges are each one stop away.",
    },
    {
      question: "Is it the same as Chuan Park?",
      answer:
        "No. Chuan Park is a separate 916-unit condo by Kingsford next to Lorong Chuan MRT. It launched in November 2024. Chuan Grove GLS has a different developer and has not launched.",
    },
    {
      question: "Can foreigners buy?",
      answer:
        "Yes. It is a private condo. Additional Buyer's Stamp Duty applies. Check IRAS for the current rates.",
    },
  ],
  "sembawang-ec": [
    {
      question: "What is Sembawang Road EC?",
      answer:
        "An upcoming executive condominium on Sembawang Road in District 27, with about 265 homes on a 99-year lease. Oriental Pacific Development won the site in September 2025.",
    },
    {
      question: "Who is the developer?",
      answer:
        "Oriental Pacific Development Pte. Ltd., part of Oriental Pacific Holdings. Its founder, Jane Lam, was previously with JBE Holdings, which developed The Commodore nearby.",
    },
    {
      question: "Is it the same as Sembawang Drive EC?",
      answer:
        "No. Sembawang Drive EC is a separate, newer site with a 10-year MOP. Sembawang Road EC keeps the 5-year MOP.",
    },
    {
      question: "What is the MOP and income ceiling?",
      answer:
        "A 5-year MOP and a S$16,000 monthly household income ceiling. The tender closed on 11 September 2025, before both rule changes in 2026.",
    },
    {
      question: "Is the Deferred Payment Scheme available?",
      answer:
        "The rules allow it for this site, but the developer has not said whether it will offer it. We will update this page when the price list is out.",
    },
    {
      question: "How much will it cost?",
      answer:
        "Prices have not been released. PropNex expects an average above S$1,700 psf, based on the land price. That is a projection, not a price list.",
    },
    {
      question: "What is the project called, and when does it launch?",
      answer:
        "The developer has not announced a name or launch date. Press reports expect early 2027. TOP has not been announced either.",
    },
    {
      question: "How far is it from the MRT?",
      answer:
        "Canberra MRT on the North-South Line is the nearest station. We are confirming the walking distance from the site and will add it here.",
    },
    {
      question: "Can foreigners buy?",
      answer:
        "No. Foreigners cannot buy a new EC. It can be sold to foreigners only after it is privatised, 10 years after TOP.",
    },
  ],
  "upper-thomson-gls-parcel-a": [
    {
      question: "What is Upper Thomson GLS Parcel A?",
      answer:
        "A 99-year residential site with ground-floor shops, next to Springleaf MRT. The government sold it in October 2025. It will be developed into a private condo of about 595 homes.",
    },
    {
      question: "Who is the developer?",
      answer:
        "Wee Hur Property Pte Ltd and GSC Holdings Pte Ltd, part of the Wee Hur group. They won the site with a S$613.9 million bid.",
    },
    {
      question: "What is the project called?",
      answer:
        "The developer has not announced a name. Names on agent websites, such as \"Upper Thomson Residences\", are not official. We will update this page when the developer confirms one.",
    },
    {
      question: "Is it the same as Springleaf Residence?",
      answer:
        "No. Springleaf Residence is Parcel B, next door, by GuocoLand and Hong Leong. It launched in August 2025. Parcel A is a separate site with a different developer, and it has not launched.",
    },
    {
      question: "How much will it cost?",
      answer:
        "Prices have not been released. Analysts expect an average of about S$2,300 psf or more, based on the land price (October 2025). That is a projection, not a price list.",
    },
    {
      question: "When does it launch, and when is TOP?",
      answer:
        "Neither has been announced. Analysts expected a launch around 2027. Register on this page to be told when the preview opens.",
    },
    {
      question: "Are there primary schools nearby?",
      answer:
        "No primary school is within 1 km. The nearest are about 2.5 to 3 km away in Yishun and Ang Mo Kio. Check exact distances on OneMap.",
    },
    {
      question: "Can foreigners buy?",
      answer:
        "Yes. It is a private condo. Additional Buyer's Stamp Duty applies. Check IRAS for the current rates.",
    },
  ],
  "bedok-rise-gls": [
    {
      question: "What is the Bedok Rise GLS?",
      answer:
        "A 99-year residential site in District 16 next to Tanah Merah MRT, sold by the government in December 2025. It will become a private condo of about 380 homes.",
    },
    {
      question: "Who is the developer?",
      answer:
        "Bellis Residential Pte. Ltd., which won the site with a S$464.8 million bid. ERA and EdgeProp report it as an Allgreen Properties company.",
    },
    {
      question: "What is the project called?",
      answer:
        "The developer has not announced a name. Names such as \"Bedok Rise Residences\" on other websites are not official. We will update this page when the name is out.",
    },
    {
      question: "How much will it cost?",
      answer:
        "Prices have not been released. Analysts project an average of about S$2,300 to S$2,700 psf, based on the land price. That is a projection, not a price list.",
    },
    {
      question: "When does it launch, and when is TOP?",
      answer:
        "Neither has been announced. Register on this page to be told when the preview opens.",
    },
    {
      question: "How close is it to the MRT?",
      answer:
        "The site is next to Tanah Merah MRT on the East-West Line. A Thomson-East Coast Line interchange at Tanah Merah is planned for the mid-2030s.",
    },
    {
      question: "How does it compare with Sceneca Residence?",
      answer:
        "Sceneca is across the road and fully sold. Its land cost about S$930 psf ppr in 2020, against about S$1,330 psf ppr for Bedok Rise, so expect higher prices here.",
    },
    {
      question: "Can foreigners buy?",
      answer:
        "Yes. It is a private condo. Additional Buyer's Stamp Duty applies. Check IRAS for the current rates.",
    },
  ],
  "telok-blangah-gls": [
    {
      question: "What is the Telok Blangah Road GLS?",
      answer:
        "A 99-year private residential site in District 4, on former Keppel Club land, sold by the government in November 2025. It will be developed into a condo of about 745 homes.",
    },
    {
      question: "Who is the developer?",
      answer:
        "Kingsford Huray Development Pte Ltd, part of Kingsford Group, which developed Normanton Park. It won the site with a S$918.3 million bid.",
    },
    {
      question: "What is the project called?",
      answer:
        "The developer has not announced a name. Names on other websites, such as \"Telok Blangah Residences\", are not confirmed. We will update this page when Kingsford announces one.",
    },
    {
      question: "How much will it cost?",
      answer:
        "Prices have not been released. Analysts at CBRE and PropNex expect an average of about S$2,700 to S$2,800 psf or more, based on the land price. That is a projection, not a price list.",
    },
    {
      question: "When does it launch, and when is TOP?",
      answer:
        "Neither has been announced. Register on this page to be told when the preview opens.",
    },
    {
      question: "How close is it to the MRT?",
      answer:
        "It is about a 5-minute walk to Telok Blangah MRT on the Circle Line. HarbourFront, an interchange with the North East Line, is one stop away.",
    },
    {
      question: "Is it the same as Berlayar Drive GLS or Berlayar Residences?",
      answer:
        "No. Berlayar Drive is a separate, low-rise private site nearby, by a Hong Leong Holdings and GuocoLand joint venture. Berlayar Residences is an HDB BTO project.",
    },
    {
      question: "Can foreigners buy?",
      answer:
        "Yes. It is a private condo. Additional Buyer's Stamp Duty applies. Check IRAS for the current rates.",
    },
  ],
};

/** Case-insensitive lookup, so a capitalised slug still matches. */
export function propertyFaqOverride(
  slug: string | undefined | null,
): PropertyFaqOverride[] | null {
  if (!slug) return null;
  const key = decodeURIComponent(slug).trim().toLowerCase();
  return PROPERTY_FAQS[key] ?? null;
}
